/**
 * Fires one referral-click beacon per browser session per partner slug.
 *
 * Why a client beacon rather than a /r/:slug redirect route: WhatsApp, Telegram
 * and iMessage all prefetch links to build previews, so a server-side redirect
 * would count a click for every share of the link, not every visit. It also
 * keeps every ?ref= link an affiliate has already handed out working unchanged.
 *
 * The cost of that choice is that counts only exist from launch forward, and a
 * visitor with JavaScript disabled is never counted. Both are acceptable: the
 * number is a trust signal for the partner, not the thing they're paid on.
 */
import { leadsApi } from '@/services/leadsApi';
import { readRefSlug } from '@/utils/attributionTracking';

const SESSION_KEY_PREFIX = 'dt:refclick:';

/**
 * Reads the ref slug from the current URL and counts it once per session.
 * Never throws and never blocks: a failed beacon must not affect the page a
 * visitor came to see.
 */
export const trackRefClickOnce = (pagePartnerSlug?: string): void => {
  if (typeof window === 'undefined') return;

  try {
    const search = window.location?.search || '';
    const ref = pagePartnerSlug || readRefSlug(new URLSearchParams(search));
    if (!ref) return;

    const sessionKey = `${SESSION_KEY_PREFIX}${ref}`;
    if (window.sessionStorage?.getItem(sessionKey)) return;

    // Marked before the request resolves, and left marked even if it fails. A
    // retry loop on a flaky connection would over-count a single visit, and
    // over-counting is the failure mode that costs an affiliate's trust.
    window.sessionStorage?.setItem(sessionKey, '1');

    void leadsApi
      .trackRefClick(
        ref,
        window.location?.href?.substring(0, 500) || null,
        document.referrer?.substring(0, 500) || null
      )
      .catch(() => {
        // Fire-and-forget: swallow transport and server errors alike.
      });
  } catch {
    // sessionStorage can throw in private-mode Safari and inside sandboxed
    // iframes. A visitor whose browser refuses storage simply isn't counted.
  }
};
